import { Injectable, signal, computed, effect, inject } from '@angular/core';
import { Ticket } from '../../models/shopping-cart';
import { FleetStateService } from './fleet-state.service';
import { LoggingService } from '@fleet-monitor/frontend-logging';

/**
 * Shopping Cart State Service
 *
 * Manages shopping cart state using Angular Signals.
 * Persists cart data to localStorage for persistence across sessions.
 *
 * Follows the same pattern as FleetStateService for consistency.
 */
@Injectable({
  providedIn: 'root',
})
export class ShoppingCartStateService {
  private fleetState = inject(FleetStateService);
  private logger = inject(LoggingService);

  // Cart limits
  private readonly MAX_CART_SIZE = 50;
  private readonly WARNING_THRESHOLD = 45;

  // Local storage keys
  private readonly STORAGE_KEYS = {
    ITEMS: 'shopping_cart_items',
    PAYMENT_PROFILE: 'shopping_cart_payment_profile',
  };

  // State signals - raw cart items from storage (all fleets)
  private allCartItems = signal<Ticket[]>(this.loadCartFromStorage());
  selectedPaymentProfileId = signal<string | null>(
    this.loadPaymentProfileFromStorage()
  );

  // Computed property - filtered cart items for active fleet only
  cartItems = computed(() => {
    const activeFleetId = this.fleetState.activeFleetId();
    const allItems = this.allCartItems();

    // If no active fleet, return empty cart
    if (!activeFleetId) {
      return [];
    }

    // Filter to only show tickets from the active fleet
    return allItems.filter((ticket) => ticket.fleet === activeFleetId);
  });

  // Computed properties
  itemCount = computed(() => this.cartItems().length);

  ticketTotal = computed(() => {
    return this.cartItems().reduce((sum, ticket) => {
      return sum + (ticket.amount ?? 0);
    }, 0);
  });

  feesTotal = computed(() => {
    return this.cartItems().reduce((sum, ticket) => {
      return sum + (ticket.mf_fee ?? 0);
    }, 0);
  });

  vendorFeesTotal = computed(() => {
    return this.cartItems().reduce((sum, ticket) => {
      return sum + (ticket.fees ?? 0);
    }, 0);
  });

  grandTotal = computed(() => {
    return this.cartItems().reduce((sum, ticket) => {
      const total = ticket.total ?? ticket.amount ?? 0;
      return sum + total;
    }, 0);
  });

  constructor() {
    this.logger.log('[ShoppingCartState] Service initialized');

    // Effect to persist all cart items to localStorage (across all fleets)
    effect(() => {
      const items = this.allCartItems();
      this.logger.log(
        '[ShoppingCartState] Cart items changed, persisting to localStorage',
        {
          itemCount: items.length,
          ticketIds: items.map((t) => ({
            id: t._id,
            ticket_id: t.ticket_id,
            fleet: t.fleet,
          })),
        }
      );
      try {
        localStorage.setItem(this.STORAGE_KEYS.ITEMS, JSON.stringify(items));
      } catch (error) {
        this.logger.error(
          '[ShoppingCartState] Failed to persist cart to localStorage',
          error
        );
        // Silent fail - localStorage may be unavailable
      }
    });

    // Effect to persist payment profile to localStorage
    effect(() => {
      const profileId = this.selectedPaymentProfileId();
      try {
        if (profileId) {
          localStorage.setItem(this.STORAGE_KEYS.PAYMENT_PROFILE, profileId);
        } else {
          localStorage.removeItem(this.STORAGE_KEYS.PAYMENT_PROFILE);
        }
      } catch (error) {
        // Silent fail - localStorage may be unavailable
      }
    });

    // Listen for storage events from other tabs for cross-tab synchronization
    this.setupCrossTabSync();
  }

  /**
   * Setup cross-tab synchronization using storage events
   * When cart is modified in another tab, sync the changes to this tab
   */
  private setupCrossTabSync(): void {
    if (typeof window === 'undefined') {
      return; // Skip if not in browser environment
    }

    window.addEventListener('storage', (event: StorageEvent) => {
      // Only handle changes to our cart storage keys
      if (event.key === this.STORAGE_KEYS.ITEMS && event.newValue) {
        try {
          const newItems = JSON.parse(event.newValue) as Ticket[];
          this.logger.log('[ShoppingCartState] Cart updated in another tab, syncing...', {
            newItemCount: newItems.length
          });

          // Update local state with new items from other tab
          this.allCartItems.set(newItems);

          // Could emit a notification here if needed
          this.logger.log('[ShoppingCartState] Cart synchronized from other tab');
        } catch (error) {
          this.logger.error('[ShoppingCartState] Failed to sync cart from other tab', error);
        }
      } else if (event.key === this.STORAGE_KEYS.PAYMENT_PROFILE) {
        const newProfileId = event.newValue;
        this.logger.log('[ShoppingCartState] Payment profile updated in another tab, syncing...', {
          newProfileId
        });

        // Update local state with new profile from other tab
        this.selectedPaymentProfileId.set(newProfileId);
      }
    });

    this.logger.log('[ShoppingCartState] Cross-tab synchronization enabled');
  }

  /**
   * Add a single ticket to the cart
   *
   * @param ticket The ticket to add
   * @returns true if added, false if already in cart or not payable
   */
  addToCart(ticket: Ticket): boolean {
    const activeFleetId = this.fleetState.activeFleetId();
    const currentSize = this.cartItems().length;

    this.logger.log('[ShoppingCartState] addToCart called', {
      ticketId: ticket._id,
      ticket_id: ticket.ticket_id,
      canPay: ticket.canPay,
      fleet: activeFleetId,
      currentCartSize: currentSize,
    });

    // Validate active fleet exists
    if (!activeFleetId) {
      this.logger.log('[ShoppingCartState] No active fleet selected');
      return false;
    }

    // Check cart size limit
    if (currentSize >= this.MAX_CART_SIZE) {
      this.logger.warn('[ShoppingCartState] Cart is full', {
        currentSize,
        maxSize: this.MAX_CART_SIZE
      });
      return false;
    }

    // Warn when approaching limit
    if (currentSize >= this.WARNING_THRESHOLD && currentSize < this.MAX_CART_SIZE) {
      this.logger.warn('[ShoppingCartState] Approaching cart limit', {
        currentSize,
        maxSize: this.MAX_CART_SIZE
      });
    }

    // Validate ticket can be paid
    // Only reject if canPay is explicitly false (allow null/undefined)
    if (ticket.canPay === false) {
      this.logger.log('[ShoppingCartState] Ticket cannot be paid', {
        ticketId: ticket._id,
        canPay: ticket.canPay,
      });
      return false;
    }

    // Check for duplicates
    const inCart = this.isInCart(ticket._id);
    this.logger.log('[ShoppingCartState] Duplicate check', {
      ticketId: ticket._id,
      isInCart: inCart,
      currentCartIds: this.cartItems().map((t) => t._id),
    });

    if (inCart) {
      this.logger.log('[ShoppingCartState] Ticket already in cart', {
        ticketId: ticket._id,
      });
      return false;
    }

    // Add fleet ID to ticket and add to cart
    const ticketWithFleet = { ...ticket, fleet: activeFleetId };
    const current = this.allCartItems();
    this.allCartItems.set([...current, ticketWithFleet]);
    this.logger.log('[ShoppingCartState] Ticket added successfully', {
      ticketId: ticket._id,
      fleet: activeFleetId,
      newCartSize: this.cartItems().length,
    });
    return true;
  }

  /**
   * Add multiple tickets to the cart at once
   * Filters out unpayable and duplicate tickets
   * Respects cart size limits
   *
   * @param tickets Array of tickets to add
   * @returns Array of ticket IDs that were successfully added
   */
  addMultipleToCart(tickets: Ticket[]): string[] {
    const activeFleetId = this.fleetState.activeFleetId();
    const currentSize = this.cartItems().length;

    this.logger.log('[ShoppingCartState] addMultipleToCart called', {
      ticketCount: tickets.length,
      ticketIds: tickets.map((t) => ({
        id: t._id,
        ticket_id: t.ticket_id,
        canPay: t.canPay,
      })),
      fleet: activeFleetId,
      currentCartSize: currentSize,
    });

    // Validate active fleet exists
    if (!activeFleetId) {
      this.logger.log('[ShoppingCartState] No active fleet selected');
      return [];
    }

    // Check available space in cart
    const available = this.MAX_CART_SIZE - currentSize;
    if (available === 0) {
      this.logger.warn('[ShoppingCartState] Cart is full', {
        currentSize,
        maxSize: this.MAX_CART_SIZE
      });
      return [];
    }

    const current = this.allCartItems();
    const currentIds = new Set(current.map((t) => t._id));

    this.logger.log('[ShoppingCartState] Current cart IDs', {
      currentIds: Array.from(currentIds),
    });

    // Filter to only payable tickets that aren't already in cart
    let validTickets = tickets.filter((ticket: Ticket) => {
      // Only reject if canPay is explicitly false (allow null/undefined)
      const isPayable = ticket.canPay !== false;
      const isDuplicate = currentIds.has(ticket._id);

      this.logger.log('[ShoppingCartState] Validating ticket', {
        ticketId: ticket._id,
        ticket_id: ticket.ticket_id,
        canPay: ticket.canPay,
        isPayable,
        isDuplicate,
        willAdd: isPayable && !isDuplicate,
      });

      return isPayable && !isDuplicate;
    });

    // Limit to available space
    if (validTickets.length > available) {
      this.logger.warn('[ShoppingCartState] Limiting tickets to available space', {
        requested: validTickets.length,
        available,
        willSkip: validTickets.length - available
      });
      validTickets = validTickets.slice(0, available);
    }

    this.logger.log('[ShoppingCartState] Validation complete', {
      totalTickets: tickets.length,
      validTickets: validTickets.length,
      skipped: tickets.length - validTickets.length,
    });

    if (validTickets.length === 0) {
      this.logger.log('[ShoppingCartState] No valid tickets to add');
      return [];
    }

    // Add fleet ID to all tickets and add to cart
    const ticketsWithFleet = validTickets.map((ticket) => ({
      ...ticket,
      fleet: activeFleetId,
    }));
    this.allCartItems.set([...current, ...ticketsWithFleet]);

    this.logger.log('[ShoppingCartState] Multiple tickets added successfully', {
      addedCount: validTickets.length,
      newCartSize: this.cartItems().length,
    });

    return validTickets.map((t) => t._id);
  }

  /**
   * Remove a ticket from the cart
   *
   * @param ticketId The ID of the ticket to remove
   */
  removeFromCart(ticketId: string): void {
    const current = this.allCartItems();
    const filtered = current.filter((ticket) => ticket._id !== ticketId);
    this.allCartItems.set(filtered);
  }

  /**
   * Clear all items from the cart for the active fleet
   */
  clearCart(): void {
    const activeFleetId = this.fleetState.activeFleetId();
    if (!activeFleetId) {
      return;
    }

    // Remove only tickets from the active fleet
    const current = this.allCartItems();
    const filtered = current.filter((ticket) => ticket.fleet !== activeFleetId);
    this.allCartItems.set(filtered);
  }

  /**
   * Check if a ticket is in the cart (for the active fleet)
   *
   * @param ticketId The ID of the ticket to check
   * @returns true if ticket is in cart
   */
  isInCart(ticketId: string): boolean {
    return this.cartItems().some((ticket) => ticket._id === ticketId);
  }

  /**
   * Update tickets in cart with new data (e.g., after fee recalculation)
   * Only updates tickets from the active fleet
   *
   * @param updatedTickets Array of updated ticket objects
   */
  updateCartItems(updatedTickets: Ticket[]): void {
    const activeFleetId = this.fleetState.activeFleetId();
    if (!activeFleetId) {
      return;
    }

    const current = this.allCartItems();
    const updatedMap = new Map(updatedTickets.map((t) => [t._id, t]));

    // Merge updated ticket data with existing cart items, preserving order and fleet ID
    const merged = current.map((ticket) => {
      const updated = updatedMap.get(ticket._id);
      // Only update if ticket is from active fleet and we have an update for it
      if (updated && ticket.fleet === activeFleetId) {
        return { ...updated, fleet: activeFleetId };
      }
      return ticket;
    });

    this.allCartItems.set(merged);
  }

  /**
   * Set the selected payment profile
   *
   * @param profileId The payment profile ID, or null to clear
   */
  setPaymentProfile(profileId: string | null): void {
    this.selectedPaymentProfileId.set(profileId);
  }

  /**
   * Load cart items from localStorage
   *
   * @returns Array of tickets, or empty array if none found
   */
  private loadCartFromStorage(): Ticket[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEYS.ITEMS);
      // Note: Cannot use this.logger here as it's not initialized yet (called from property initializer)
      console.log('[ShoppingCartState] Loading cart from localStorage', {
        hasData: !!stored,
        rawData: stored ? stored.substring(0, 200) : null, // Log first 200 chars of raw data
      });

      if (!stored) {
        console.log('[ShoppingCartState] No cart data in localStorage');
        return [];
      }

      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed)) {
        console.log('[ShoppingCartState] Invalid cart data (not an array)', {
          parsed,
        });
        return [];
      }

      console.log('[ShoppingCartState] Loaded cart from localStorage', {
        ticketCount: parsed.length,
        ticketIds: parsed.map((t: Ticket) => ({
          id: t._id,
          ticket_id: t.ticket_id,
        })),
      });

      // If cart has items, log the first one for debugging
      if (parsed.length > 0) {
        console.log('[ShoppingCartState] First cart item sample', {
          _id: parsed[0]._id,
          ticket_id: parsed[0].ticket_id,
          canPay: parsed[0].canPay,
          state: parsed[0].state,
        });
      }

      // Return tickets as plain objects (Ticket is an interface, not a class)
      return parsed as Ticket[];
    } catch (error) {
      console.error(
        '[ShoppingCartState] Error loading cart from localStorage',
        error
      );
      // Silent fail - return empty cart if unable to load
      return [];
    }
  }

  /**
   * Load payment profile from localStorage
   *
   * @returns Payment profile ID, or null if none found
   */
  private loadPaymentProfileFromStorage(): string | null {
    try {
      return localStorage.getItem(this.STORAGE_KEYS.PAYMENT_PROFILE);
    } catch (error) {
      // Silent fail - return null if unable to load
      return null;
    }
  }
}
